import { useEffect, useState } from "react";
import {
  QrCode,
  Smartphone,
  Loader2,
  Plug,
  MessageSquare,
  RefreshCw,
  Lock,
  Briefcase,
  Building2,
  Heart,
  Users,
  User,
} from "lucide-react";
import { getCsrfToken } from "../lib/auth";
import { ScopeBadge, type Scope } from "../components/ScopePicker";
import { TeachingBanner } from "../components/TeachingBanner";

/** Android Messages connector settings + pairing.
 *
 *  Mounted at /settings/connectors/android-messages. The connector is
 *  driven by the waki-android-messages-connector sidecar (Playwright
 *  against messages.google.com/web). Pairing requires the user to scan
 *  a QR from their Android phone — the user is on a remote client and
 *  cannot see the terminal, so the on-screen flow below is the ONLY
 *  path. One page: QR + steps + scope + Connect, no gating. Read-only
 *  ingest: never sends, never marks read. */

interface PairStatus {
  ok?: boolean;
  pair_status?: string;
  paired?: boolean;
  me?: { id: string; name: string | null } | null;
  qr_available?: boolean;
  last_qr_at?: string | null;
  last_connected_at?: string | null;
  last_disconnect_reason?: string | null;
}

interface ConnectorState {
  exists: boolean;
  scope_chosen: boolean;
  privacy_scope?: string | null;
  enabled?: boolean;
  run_minted?: boolean;
  run_id?: string | null;
  message_count?: number;
  last_tick_at?: string | null;
  created_at?: string;
  updated_at?: string;
}

const SCOPE_CHOICES: Array<{ id: Scope; label: string; Icon: typeof Lock }> = [
  { id: "personal", label: "Personal", Icon: User },
  { id: "work", label: "Work", Icon: Briefcase },
  { id: "family", label: "Family", Icon: Heart },
  { id: "private", label: "Private", Icon: Lock },
  { id: "social", label: "Social", Icon: Users },
  { id: "business", label: "Business", Icon: Building2 },
];

export function AndroidMessages() {
  const [pair, setPair] = useState<PairStatus | null>(null);
  const [pairErr, setPairErr] = useState<string | null>(null);
  const [connector, setConnector] = useState<ConnectorState | null>(null);

  const refreshPair = async () => {
    try {
      const r = await fetch("/api/android-messages/v1/pair/status", {
        credentials: "same-origin",
      });
      if (!r.ok) {
        setPairErr(`pair/status -> ${r.status}`);
        return;
      }
      setPair((await r.json()) as PairStatus);
      setPairErr(null);
    } catch (e) {
      setPairErr((e as Error).message);
    }
  };

  const refreshConnector = async () => {
    try {
      const r = await fetch("/api/android-messages/connector", {
        credentials: "same-origin",
      });
      if (!r.ok) return;
      setConnector((await r.json()) as ConnectorState);
    } catch {
      /* best effort */
    }
  };

  useEffect(() => {
    refreshPair();
    refreshConnector();
    const t = setInterval(() => {
      refreshPair();
      refreshConnector();
    }, 4_000);
    return () => clearInterval(t);
  }, []);

  const paired = pair?.paired === true;
  const runMinted = connector?.run_minted === true;
  const connected = paired && runMinted;

  return (
    <div className="w-full max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 py-6 space-y-5">
      <header className="space-y-1">
        <div className="flex items-center gap-2">
          <div className="text-xs uppercase tracking-wider font-bold text-slate-400 dark:text-slate-500">
            Connectors / Android Messages
          </div>
          {connector?.privacy_scope && <ScopeBadge scope={connector.privacy_scope} />}
        </div>
        <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
          {connected ? "Android Messages" : "Pair Google Messages from your phone"}
        </h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 max-w-2xl">
          Mirrors your SMS and MMS through a paired Google Messages for web
          session. Read-only: the connector never sends a message and never
          marks a thread read on your phone.
        </p>
      </header>

      <TeachingBanner id="android_messages_overview" />

      {pairErr && (
        <div className="glass rounded-xl border border-rose-300 dark:border-rose-700 backdrop-blur p-3 text-xs text-rose-700 dark:text-rose-300">
          Sidecar unreachable: {pairErr}. Make sure the{" "}
          <code className="bg-rose-50 dark:bg-rose-950/50 px-1 rounded">
            waki-android-messages-connector
          </code>{" "}
          launchd job is running on this Mac.
        </div>
      )}

      {connected ? (
        <ConnectedCard
          pair={pair}
          connector={connector}
          onChanged={() => {
            refreshPair();
            refreshConnector();
          }}
        />
      ) : (
        <PairCard
          pair={pair}
          existingScope={(connector?.privacy_scope as Scope | null) ?? null}
          paired={paired}
          runMinted={runMinted}
          onConnected={() => {
            refreshPair();
            refreshConnector();
          }}
        />
      )}
    </div>
  );
}

function PairCard({
  pair,
  existingScope,
  paired,
  runMinted,
  onConnected,
}: {
  pair: PairStatus | null;
  existingScope: Scope | null;
  paired: boolean;
  runMinted: boolean;
  onConnected: () => void;
}) {
  const [scope, setScope] = useState<Scope>(existingScope ?? "personal");
  const [bootBusy, setBootBusy] = useState(false);
  const [bootErr, setBootErr] = useState<string | null>(null);
  const [bootOk, setBootOk] = useState<string | null>(null);
  const [qrTick, setQrTick] = useState(0);

  useEffect(() => {
    if (existingScope) setScope(existingScope);
  }, [existingScope]);

  useEffect(() => {
    const t = setInterval(() => setQrTick((x) => x + 1), 3_000);
    return () => clearInterval(t);
  }, []);

  const triggerBootstrap = async () => {
    setBootBusy(true);
    setBootErr(null);
    setBootOk(null);
    const csrf = getCsrfToken();
    try {
      const r = await fetch("/api/android-messages/bootstrap", {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          ...(csrf ? { "X-CSRF-Token": csrf } : {}),
        },
        body: JSON.stringify({ privacy_scope: scope }),
      });
      const j = await r.json();
      if (!r.ok) {
        setBootErr(j.message || j.hint || j.error || `bootstrap -> ${r.status}`);
        return;
      }
      setBootOk(
        `Connected. Run ${String(j.run_id ?? "").slice(0, 12)}… is shipping messages to Memso.`,
      );
      onConnected();
    } catch (e) {
      setBootErr((e as Error).message);
    } finally {
      setBootBusy(false);
    }
  };

  const isQrReady = !paired && pair?.pair_status === "qr" && pair?.qr_available;

  return (
    <section className="space-y-4">
      <div className="glass rounded-xl border border-slate-200/70 dark:border-slate-700/50 backdrop-blur p-5 space-y-5">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-sky-500 text-white flex items-center justify-center ring-2 ring-sky-300/40">
            <QrCode className="w-5 h-5" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100">
              Pair Google Messages from your phone
            </h2>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              {paired
                ? "Your phone is linked. One tap left: click Connect to start ingesting."
                : "Scan the QR with the Google Messages app on your phone, pick a scope, then click Connect."}
              {pair?.last_disconnect_reason && !paired && (
                <>
                  {" "}
                  · last disconnect:{" "}
                  <span className="font-mono">{pair.last_disconnect_reason}</span>
                </>
              )}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-5 items-start">
          <div className="rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-2 inline-flex items-center justify-center">
            {paired ? (
              <div className="w-[240px] h-[240px] flex flex-col items-center justify-center gap-2 text-center px-4">
                <div className="w-12 h-12 rounded-full bg-sky-500 text-white flex items-center justify-center">
                  <Smartphone className="w-6 h-6" />
                </div>
                <div className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                  Linked as {pair?.me?.name || pair?.me?.id || "your phone"}
                </div>
                <div className="text-xs text-slate-500 dark:text-slate-400">
                  Device paired. No QR scan needed.
                </div>
              </div>
            ) : isQrReady ? (
              <img
                key={qrTick}
                src={`/api/android-messages/v1/pair/qr.png?t=${qrTick}`}
                alt="Android Messages pairing QR"
                width={240}
                height={240}
                className="block"
              />
            ) : (
              <div className="w-[240px] h-[240px] flex items-center justify-center text-xs text-slate-500 dark:text-slate-400 text-center px-4">
                {pair?.pair_status === "starting"
                  ? "Sidecar booting a Chrome window on messages.google.com…"
                  : pair?.pair_status === "loggedOut"
                    ? "Messages for web logged us out. Generating a fresh QR…"
                    : "Waiting for QR…"}
              </div>
            )}
          </div>

          <ol className="text-sm text-slate-700 dark:text-slate-200 space-y-2.5 list-decimal list-outside ml-4">
            <li className={paired ? "text-slate-400 dark:text-slate-500 line-through" : ""}>
              Open the <span className="font-semibold">Google Messages</span> app
              on your Android phone.
            </li>
            <li className={paired ? "text-slate-400 dark:text-slate-500 line-through" : ""}>
              Tap your <span className="font-semibold">profile picture</span> in
              the top-right corner.
            </li>
            <li className={paired ? "text-slate-400 dark:text-slate-500 line-through" : ""}>
              Tap <span className="font-semibold">"Device pairing"</span>{" "}
              <span className="text-xs text-slate-500 dark:text-slate-400">
                (or, on older versions, look in Settings → Device pairing or
                Settings → Linked devices)
              </span>
              .
            </li>
            <li className={paired ? "text-slate-400 dark:text-slate-500 line-through" : ""}>
              Tap <span className="font-semibold">"QR code scanner"</span>.
            </li>
            <li className={paired ? "text-slate-400 dark:text-slate-500 line-through" : ""}>
              Scan the QR code shown here.
            </li>
            <li>
              Pick a scope{" "}
              <span className="text-xs text-slate-500 dark:text-slate-400">
                (default Personal)
              </span>
              :
              <div className="mt-2 flex flex-wrap gap-1.5">
                {SCOPE_CHOICES.map((o) => {
                  const active = scope === o.id;
                  return (
                    <button
                      key={o.id}
                      type="button"
                      onClick={() => setScope(o.id)}
                      aria-pressed={active}
                      className={
                        "inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-semibold border-2 transition-all " +
                        (active
                          ? "border-sky-500 bg-sky-500/10 text-sky-700 dark:text-sky-200"
                          : "border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:border-sky-400")
                      }
                    >
                      <o.Icon className="w-3.5 h-3.5" />
                      {o.label}
                    </button>
                  );
                })}
              </div>
            </li>
            <li className="font-semibold">
              <button
                type="button"
                onClick={triggerBootstrap}
                disabled={bootBusy}
                className="inline-flex items-center gap-1.5 rounded-md bg-sky-600 hover:bg-sky-700 disabled:opacity-60 text-white text-sm font-semibold px-4 py-2"
              >
                {bootBusy ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Plug className="w-4 h-4" />
                )}
                Connect
              </button>
              <div className="text-xs font-normal text-slate-500 dark:text-slate-400 mt-1">
                {paired
                  ? "Your phone is linked. Click to finish connecting and start ingesting."
                  : "Safe to click before or after scanning. Once the phone is linked, messages start flowing within ~30 seconds."}
              </div>
              {bootOk && (
                <div className="text-xs font-normal text-sky-600 dark:text-sky-300 mt-1">
                  {bootOk}
                </div>
              )}
              {bootErr && (
                <div className="text-xs font-normal text-rose-600 dark:text-rose-300 mt-1">
                  {bootErr}
                </div>
              )}
            </li>
          </ol>
        </div>

        <p className="text-xs text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 rounded-lg px-3 py-2">
          If you don't see "Device pairing" or "QR code scanner" in the Messages
          app, your Google Messages version may be too old. Update it via the
          Play Store and try again.
        </p>

        <p className="text-[11px] text-slate-400 dark:text-slate-500">
          The sidecar keeps the session under{" "}
          <code className="text-[11px] bg-slate-100 dark:bg-slate-800 px-1 rounded">
            ~/.memso/android-messages-profile
          </code>
          ; restarts will not re-prompt unless your phone is offline for a long
          stretch. v1 is read-only: no read receipts, no sending.
        </p>
      </div>
    </section>
  );
}

function ConnectedCard({
  pair,
  connector,
  onChanged,
}: {
  pair: PairStatus | null;
  connector: ConnectorState | null;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState<"none" | "disconnect" | "repair">("none");
  const [err, setErr] = useState<string | null>(null);

  const post = async (path: string, kind: "disconnect" | "repair") => {
    setBusy(kind);
    setErr(null);
    const csrf = getCsrfToken();
    try {
      const r = await fetch(path, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          ...(csrf ? { "X-CSRF-Token": csrf } : {}),
        },
        body: JSON.stringify({}),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        setErr(j.message || j.error || `${path} -> ${r.status}`);
        return;
      }
      onChanged();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy("none");
    }
  };

  return (
    <section className="space-y-4">
      <div className="glass rounded-xl border border-sky-200 dark:border-sky-800 backdrop-blur p-5 space-y-4">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-sky-500 text-white flex items-center justify-center">
            <MessageSquare className="w-5 h-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100">
                Connected
              </h2>
              {connector?.privacy_scope && (
                <ScopeBadge scope={connector.privacy_scope} />
              )}
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              Paired as{" "}
              <span className="font-semibold">
                {pair?.me?.name || pair?.me?.id || "your phone"}
              </span>
              {pair?.last_connected_at && (
                <> since {new Date(pair.last_connected_at).toLocaleString()}</>
              )}
              .
            </p>
          </div>
        </div>

        <dl className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
          <Stat label="Phone number" value={pair?.me?.id || "—"} />
          <Stat
            label="Messages ingested"
            value={String(connector?.message_count ?? 0)}
          />
          <Stat
            label="Last message"
            value={
              connector?.last_tick_at
                ? new Date(connector.last_tick_at).toLocaleString()
                : "—"
            }
          />
          <Stat
            label="Run"
            value={connector?.run_id ? connector.run_id.slice(0, 12) + "…" : "—"}
          />
        </dl>

        {err && (
          <div className="text-xs text-rose-600 dark:text-rose-300">{err}</div>
        )}

        <div className="flex flex-wrap gap-2 pt-1">
          <button
            type="button"
            onClick={() => post("/api/android-messages/disconnect", "disconnect")}
            disabled={busy !== "none"}
            className="inline-flex items-center gap-1 rounded-md border border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-60 text-xs font-semibold px-3 py-1.5 text-slate-700 dark:text-slate-200"
          >
            {busy === "disconnect" && (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            )}
            Disconnect
          </button>
          <button
            type="button"
            onClick={() => post("/api/android-messages/disconnect", "repair")}
            disabled={busy !== "none"}
            className="inline-flex items-center gap-1 rounded-md border border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-60 text-xs font-semibold px-3 py-1.5 text-slate-700 dark:text-slate-200"
          >
            {busy === "repair" ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <RefreshCw className="w-3.5 h-3.5" />
            )}
            Pair another device
          </button>
        </div>
        <p className="text-[11px] text-slate-400 dark:text-slate-500">
          Disconnect clears the paired session on this Mac. Your phone's SMS
          store and Google Messages account are upstream and untouched.
          Already-ingested messages stay in Memso.
        </p>
      </div>
    </section>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-[11px] uppercase tracking-wide text-slate-400 dark:text-slate-500">
        {label}
      </dt>
      <dd className="text-sm font-semibold text-slate-800 dark:text-slate-100 truncate">
        {value}
      </dd>
    </div>
  );
}
